/**
 * 生态系统视图
 * 层面三：生态与数据护城河
 */

import React from 'react';
import { Zap, Users, Database, TrendingUp } from 'lucide-react';
import MetricCard from '../charts/MetricCard';
import CompositeMetricGauge from '../charts/CompositeMetricGauge';
import TrendChart from '../charts/TrendChart';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
// Using any for now to fix TypeScript compilation
type EcosystemMetrics = any;
type TimeSeriesDataPoint = any; 
type TrendAnalysis = any;

interface EcosystemViewProps {
  ecosystem: EcosystemMetrics;
  timeSeries: {
    dataFlywheelVelocity: TimeSeriesDataPoint[];
  };
  trends: {
    dataFlywheelVelocity: TrendAnalysis;
  };
}

const EcosystemView: React.FC<EcosystemViewProps> = ({
  ecosystem,
  timeSeries,
  trends
}) => {
  // 数据飞轮组件分解
  const flywheelComponents = [
    {
      name: '用户增长率',
      value: ecosystem.details.weeklyActiveUserGrowthRate * 100, // 转换为百分比显示
      weight: 0.6,
      color: '#3b82f6' // blue
    },
    {
      name: '平均贡献量',
      value: ecosystem.details.averageContributionsPerActiveUser,
      weight: 0.4,
      color: '#10b981' // green
    }
  ];

  // 获取数据飞轮速度告警
  const getFlywheelAlert = () => {
    if (ecosystem.dataFlywheelVelocityIndex < 0.1) {
      return {
        level: 'critical' as const,
        message: '数据飞轮几乎停滞，生态系统增长面临风险'
      };
    }
    if (ecosystem.dataFlywheelVelocityIndex < 0.5) {
      return {
        level: 'warning' as const,
        message: '数据飞轮速度偏慢，需要激励用户贡献'
      };
    }
    return undefined;
  };

  // 获取贡献者集中度告警
  const getConcentrationAlert = () => {
    if (ecosystem.contributorConcentrationRatio > 0.8) {
      return {
        level: 'critical' as const,
        message: '贡献过度集中，存在生态系统脆弱性风险'
      };
    }
    if (ecosystem.contributorConcentrationRatio > 0.6) {
      return {
        level: 'warning' as const,
        message: '贡献集中度较高，建议扩大贡献者基础'
      };
    }
    return undefined;
  };

  // 贡献者分布饼图数据
  const contributorDistributionPieData = [
    {
      name: 'Top 10%',
      value: ecosystem.contributorDistribution.find((d: any) => d.percentile === 90)?.contributionVolume || 0,
      color: '#ef4444'
    },
    {
      name: '10%-25%',
      value: (ecosystem.contributorDistribution.find((d: any) => d.percentile === 75)?.contributionVolume || 0) - 
              (ecosystem.contributorDistribution.find((d: any) => d.percentile === 90)?.contributionVolume || 0),
      color: '#f59e0b'
    },
    {
      name: '25%-50%',
      value: (ecosystem.contributorDistribution.find((d: any) => d.percentile === 50)?.contributionVolume || 0) - 
              (ecosystem.contributorDistribution.find((d: any) => d.percentile === 75)?.contributionVolume || 0),
      color: '#3b82f6'
    },
    {
      name: 'Bottom 50%',
      value: ecosystem.details.totalDataVolume - 
              (ecosystem.contributorDistribution.find((d: any) => d.percentile === 50)?.contributionVolume || 0),
      color: '#10b981'
    }
  ];

  // 贡献者分布柱状图数据
  const contributorDistributionBarData = ecosystem.contributorDistribution.map((item: any) => ({
    percentile: `Top ${item.percentile}%`,
    volume: item.contributionVolume,
    users: item.userCount
  }));

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">生态系统与数据护城河</h2>
        <p className="text-gray-600 mt-1">
          监控数据网络效应、贡献者生态和平台护城河强度
        </p>
      </div>

      {/* 核心指标卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="数据飞轮速度指数"
          value={ecosystem.dataFlywheelVelocityIndex}
          format="custom"
          subtitle="用户增长 × 贡献量"
          trend={{
            direction: trends.dataFlywheelVelocity.trend === 'increasing' ? 'up' : 
                      trends.dataFlywheelVelocity.trend === 'decreasing' ? 'down' : 'stable',
            value: Math.abs(trends.dataFlywheelVelocity.changeRate),
            label: '相比上期'
          }}
          alert={getFlywheelAlert()}
          icon={Zap}
        />

        <MetricCard
          title="贡献者集中度"
          value={ecosystem.contributorConcentrationRatio}
          format="percentage"
          subtitle="Top 10%用户的贡献占比"
          alert={getConcentrationAlert()}
          icon={Users}
        />

        <MetricCard
          title="总数据量"
          value={ecosystem.details.totalDataVolume}
          format="number"
          subtitle="累计贡献数据量"
          icon={Database}
        />

        <MetricCard
          title="周活跃用户增长率"
          value={ecosystem.details.weeklyActiveUserGrowthRate}
          format="percentage"
          subtitle="生态扩张速度"
          icon={TrendingUp}
        />
      </div>

      {/* 复合指标仪表盘 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 数据飞轮速度指数分解 */}
        <CompositeMetricGauge
          title="数据飞轮速度指数"
          value={ecosystem.dataFlywheelVelocityIndex}
          maxValue={2}
          description="衡量生态系统的自我强化循环强度"
          components={flywheelComponents}
          thresholds={{
            excellent: 1.0,
            good: 0.5,
            warning: 0.2
          }}
        />

        {/* 生态系统健康度 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">生态系统健康度</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">活跃贡献者</span>
              <span className="text-sm font-medium">
                {ecosystem.details.totalContributors.toLocaleString()}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">平均每用户贡献</span>
              <span className="text-sm font-medium">
                {ecosystem.details.averageContributionsPerActiveUser.toFixed(2)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Top 10% 贡献量</span>
              <span className="text-sm font-medium">
                {ecosystem.details.top10PercentDataVolume.toLocaleString()}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">网络密度</span>
              <span className="text-sm font-medium">
                {ecosystem.details.totalContributors > 0 ? 
                  (ecosystem.details.totalDataVolume / ecosystem.details.totalContributors).toFixed(1) : '0'}
              </span>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="text-center">
                <div className={`text-lg font-bold ${
                  ecosystem.dataFlywheelVelocityIndex > 0.8 ? 'text-green-600' :
                  ecosystem.dataFlywheelVelocityIndex > 0.4 ? 'text-blue-600' :
                  ecosystem.dataFlywheelVelocityIndex > 0.2 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {ecosystem.dataFlywheelVelocityIndex > 0.8 ? '优秀' :
                   ecosystem.dataFlywheelVelocityIndex > 0.4 ? '健康' :
                   ecosystem.dataFlywheelVelocityIndex > 0.2 ? '一般' : '需关注'}
                </div>
                <div className="text-sm text-gray-600">整体健康评级</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 贡献者分布分析 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 贡献者分布饼图 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">贡献者分布</h3>
          <div className="flex items-center">
            <div className="w-48 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={contributorDistributionPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {contributorDistributionPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => [value.toLocaleString(), '贡献量']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="flex-1 ml-6">
              <div className="space-y-3">
                {contributorDistributionPieData.map((item, _index) => (
                  <div key={item.name} className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-gray-700 flex-1">{item.name}</span>
                    <span className="text-sm font-medium">
                      {item.value.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-200 text-sm text-gray-600">
                集中度指数: <span className="font-medium">
                  {(ecosystem.contributorConcentrationRatio * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 贡献者帕累托图 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">贡献者帕累托分布</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={contributorDistributionBarData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis 
                dataKey="percentile" 
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip 
                formatter={(value: number, name: string) => [
                  value.toLocaleString(),
                  name === 'volume' ? '贡献量' : '用户数'
                ]}
              />
              <Bar dataKey="volume" fill="#3b82f6" name="volume" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 数据飞轮趋势 */}
      <TrendChart
        title="数据飞轮速度指数趋势"
        data={timeSeries.dataFlywheelVelocity}
        chartType="area"
        color="#6366f1"
        formatValue={(value) => value.toFixed(3)}
        formatDate={(timestamp) => new Date(timestamp).toLocaleDateString('zh-CN', { 
          month: 'short', 
          day: 'numeric' 
        })}
        trendAnalysis={trends.dataFlywheelVelocity}
        thresholds={{
          warning: 0.5,
          critical: 0.2
        }}
      />

      {/* 网络效应指标 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 数据价值密度 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">数据价值密度</h3>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {(ecosystem.details.totalDataVolume / Math.max(ecosystem.details.totalContributors, 1)).toFixed(1)}
            </div>
            <div className="text-sm text-gray-600 mb-4">每用户平均贡献</div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">总数据量</span>
                <span className="font-medium">{ecosystem.details.totalDataVolume.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">活跃用户</span>
                <span className="font-medium">{ecosystem.details.totalContributors.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 生态扩张速度 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">生态扩张速度</h3>
          <div className="text-center">
            <div className={`text-3xl font-bold mb-2 ${
              ecosystem.details.weeklyActiveUserGrowthRate > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {ecosystem.details.weeklyActiveUserGrowthRate > 0 ? '+' : ''}{(ecosystem.details.weeklyActiveUserGrowthRate * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 mb-4">周活跃用户增长</div>
            
            <div className="text-xs text-gray-500">
              {ecosystem.details.weeklyActiveUserGrowthRate > 0.1 ? '快速增长' :
               ecosystem.details.weeklyActiveUserGrowthRate > 0.05 ? '稳定增长' :
               ecosystem.details.weeklyActiveUserGrowthRate > 0 ? '缓慢增长' :
               ecosystem.details.weeklyActiveUserGrowthRate > -0.05 ? '轻微下降' : '显著下降'}
            </div>
          </div>
        </div>

        {/* 护城河强度 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">护城河强度</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-700">网络效应</span>
              <div className="flex items-center">
                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${Math.min(ecosystem.dataFlywheelVelocityIndex * 50, 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-600">
                  {Math.min(ecosystem.dataFlywheelVelocityIndex * 50, 100).toFixed(0)}%
                </span>
              </div>
            </div>

            <div className="flex justify-between">
              <span className="text-sm text-gray-700">数据护城河</span>
              <div className="flex items-center">
                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${Math.min(ecosystem.details.totalDataVolume / 1000, 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-600">
                  {Math.min(ecosystem.details.totalDataVolume / 1000, 100).toFixed(0)}%
                </span>
              </div>
            </div>

            <div className="flex justify-between">
              <span className="text-sm text-gray-700">用户粘性</span>
              <div className="flex items-center">
                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ width: `${(1 - ecosystem.contributorConcentrationRatio) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-600">
                  {((1 - ecosystem.contributorConcentrationRatio) * 100).toFixed(0)}%
                </span>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-200 text-center">
              <div className={`text-sm font-medium ${
                (ecosystem.dataFlywheelVelocityIndex + (1 - ecosystem.contributorConcentrationRatio)) / 2 > 0.7 ? 'text-green-600' :
                (ecosystem.dataFlywheelVelocityIndex + (1 - ecosystem.contributorConcentrationRatio)) / 2 > 0.5 ? 'text-blue-600' :
                'text-yellow-600'
              }`}>
                护城河强度: {(((ecosystem.dataFlywheelVelocityIndex + (1 - ecosystem.contributorConcentrationRatio)) / 2) * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 战略建议 */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">生态系统优化策略</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">数据飞轮加速</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {ecosystem.dataFlywheelVelocityIndex < 0.5 && (
                <li>• 推出用户贡献激励计划</li>
              )}
              {ecosystem.details.weeklyActiveUserGrowthRate < 0.05 && (
                <li>• 加强用户获取策略</li>
              )}
              <li>• 优化数据贡献体验</li>
              <li>• 建立贡献者社区</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">贡献者多样化</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {ecosystem.contributorConcentrationRatio > 0.6 && (
                <li>• 扩大贡献者基础</li>
              )}
              <li>• 降低贡献门槛</li>
              <li>• 长尾用户激活策略</li>
              <li>• 建立分层贡献机制</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">护城河加固</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• 强化网络效应</li>
              <li>• 提升数据质量</li>
              <li>• 构建生态联盟</li>
              <li>• 增强用户粘性</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcosystemView;